// @ts-check
// Autoguardado local TEMPORAL del diagrama en edición (TSK-0026).
//
// Alcance explícito y límite duro (ver ficha de tarea): esto es persistencia
// SOLO en `localStorage`, SIN cuenta de usuario y SIN llamar a ningún
// backend. El guardado en la cuenta con historial de versiones en servidor
// es TSK-0028 (bloqueada por MOD-IDENTITY). Este módulo NO importa
// `services/api-client.js` ni ningún `*-api.js`, y no hace ninguna llamada
// `fetch`/HTTP.
//
// Reutiliza la serialización ya existente (`ir-serialization.js`, TSK-0021)
// y la capa visual (`editor-visual-state.js`); no duplica esa lógica: este
// módulo solo se ocupa de leer/escribir/validar/limpiar el par
// (IR-JSON, estado visual) en `localStorage`.
//
// Seguridad y privacidad (docs/03-frontend.md §10.5): lo único que se
// guarda es el estado del propio diagrama (IR-JSON del programa + posiciones
// visuales por id). Nunca se guardan tokens, session IDs, credenciales ni
// ningún otro dato identificable. El almacenamiento nunca se trata como
// fuente de autorización ni de verdad ante el servidor (§8.5): es solo una
// comodidad de recuperación local frente a recargas accidentales.
//
// Nota de arquitectura (a confirmar por architecture-reviewer): por diseño,
// `deserializarPrograma` (ir-serialization.js, TSK-0021) regenera SIEMPRE
// `id` nuevos —el IR-JSON no lleva `id`, no es parte de la lógica del
// programa—, mientras que `editor-visual-state.js` indexa las posiciones
// precisamente por `id` de nodo. Si este módulo guardase y restaurase las
// posiciones tal cual (claves = `id` originales), tras cada recarga el
// programa restaurado tendría ids nuevos que no coincidirían con NINGUNA
// clave del estado visual guardado: la "capa visual" del autoguardado se
// perdería en silencio en cada restauración, aunque la lógica sí se
// conserve. Para evitarlo, este módulo recorre el árbol del modelo en el
// mismo orden en que `ir-serialization.js` recorre sus campos (excluyendo
// `id`/`nodo`) y usa ese ÍNDICE DE RECORRIDO ESTRUCTURAL —no el `id`— como
// clave estable entre nodo lógico y posición visual al guardar/restaurar.
// Como la lógica se conserva exactamente (ver tests de round-trip de
// ir-serialization.spec.js), el mismo recorrido produce la misma secuencia
// de índices en el árbol original y en el reconstruido. Esto usa únicamente
// el predicado `esNodo` ya público de `editor-model.js` (el mismo que ya
// usa `ir-serialization.js`); no reimplementa ninguna traducción a IR-JSON.

import { esNodo } from './editor-model.js';
// El borrador ya no es uno por usuario, sino uno por usuario Y EJERCICIO. La
// composición de la clave se comparte con los otros dos editores para que no
// puedan separarse en silencio (ver `state/ambito-borrador.js`).
import { claveConAmbito } from '../../state/ambito-borrador.js';
import { serializarPrograma, deserializarPrograma } from './ir-serialization.js';
import { estadoVisualAObjeto, estadoVisualDesdeObjeto } from './editor-visual-state.js';

/**
 * Prefijo de almacenamiento para el autoguardado local del editor. Antes de
 * esta corrección era la clave completa (fija, sin usuario): dos usuarios
 * distintos en el mismo navegador compartían el mismo diagrama guardado
 * porque nada distinguía a quién pertenecía. Se mantiene exportada con el
 * mismo nombre y mismo valor para no romper los usos existentes sin
 * `usuarioId` (tests, y cualquier futuro modo sin sesión).
 */
export const CLAVE_ALMACENAMIENTO = 'progedu.diagram-editor.autoguardado';

/**
 * Clave de almacenamiento namespaced por usuario y por EJERCICIO.
 *
 * Por usuario: cada cuenta guarda y recupera su propio diagrama, sin
 * filtrarse al siguiente que inicie sesión en el mismo navegador. Sin
 * `usuarioId` (modo sin sesión, o llamadas que no lo pasan), la clave base.
 *
 * Por ejercicio (2026-08-06): antes había UN solo diagrama por cuenta, así
 * que traducir desde Python machacaba el del ejercicio en curso y los cinco
 * ejercicios compartían lienzo. Sin `ambito` se usa el borrador libre, que es
 * el de quien no está haciendo ningún ejercicio.
 *
 * @param {string | null | undefined} usuarioId
 * @param {string | null | undefined} [ambito] Slug del ejercicio.
 * @returns {string}
 */
export function claveAlmacenamiento(usuarioId, ambito) {
  const conUsuario = usuarioId ? `${CLAVE_ALMACENAMIENTO}.${usuarioId}` : CLAVE_ALMACENAMIENTO;
  return claveConAmbito(conUsuario, ambito);
}

/**
 * Versión del esquema del envoltorio guardado en `localStorage`. Si al leer
 * la versión almacenada no coincide, los datos se consideran incompatibles
 * y se descartan (sin intento de migración): no hay versiones previas de
 * las que migrar todavía.
 */
export const VERSION_ESQUEMA = 1;

/**
 * Límite de tamaño (bytes, UTF-8) del envoltorio serializado. `localStorage`
 * suele ofrecer ~5 MiB por origen; se deja margen para otras claves de la
 * aplicación y para evitar dejar el storage al borde de la cuota con un solo
 * diagrama. Si el diagrama serializado supera este límite, NO se escribe
 * (degradación explícita) en lugar de arriesgarse a un fallo de cuota o a
 * dejar `localStorage` inservible para el resto de la app.
 */
export const LIMITE_BYTES = 4 * 1024 * 1024;

/**
 * @typedef {import('./editor-visual-state.js').EstadoVisual} EstadoVisual
 */

/**
 * @typedef {{ programa: unknown, estadoVisual: EstadoVisual }} EstadoEditorGuardable
 */

/**
 * @typedef {(
 *   | { ok: true }
 *   | { ok: false, motivo: 'no_disponible' | 'excede_limite' | 'cuota_excedida' | 'error_desconocido' }
 * )} ResultadoGuardado
 */

/**
 * Devuelve la referencia a `localStorage` si está disponible y utilizable
 * (algunos navegadores en modo privado la exponen pero lanzan en el primer
 * `setItem`; otros lanzan al simplemente acceder a la propiedad). Devuelve
 * `null` en cualquier caso de fallo, sin propagar la excepción.
 * @returns {Storage | null}
 */
function obtenerStorage() {
  try {
    const storage = globalThis.localStorage;
    if (!storage) return null;
    return storage;
  } catch {
    return null;
  }
}

/**
 * Indica si el almacenamiento local está disponible y aceptando escrituras
 * en este momento (sonda de escritura de un byte, revertida al instante).
 * Útil para que la capa de UI decida si avisar de que el autoguardado está
 * desactivado, sin necesidad de intentar guardar el diagrama real.
 * @returns {boolean}
 */
export function esAlmacenamientoDisponible() {
  const storage = obtenerStorage();
  if (!storage) return false;
  const claveSonda = '__progedu_probe__';
  try {
    storage.setItem(claveSonda, '1');
    storage.removeItem(claveSonda);
    return true;
  } catch {
    return false;
  }
}

/**
 * Recorre un nodo del modelo (o un array/primitivo anidado dentro de él) en
 * el mismo orden de campos que usa `ir-serialization.js` para producir el
 * IR-JSON (`Object.entries`, omitiendo `id`/`nodo`), invocando `visitar` con
 * el `id` real de cada nodo encontrado y un índice secuencial de recorrido
 * (0, 1, 2, ...). No traduce nombres de nodo/campo a IR-JSON: solo cuenta
 * nodos en orden, usando el predicado público `esNodo` de `editor-model.js`.
 * @param {unknown} valor
 * @param {(id: string, indice: number) => void} visitar
 * @param {{ siguiente: number }} contador
 */
function recorrerEnOrdenEstructural(valor, visitar, contador) {
  if (Array.isArray(valor)) {
    for (const elemento of valor) recorrerEnOrdenEstructural(elemento, visitar, contador);
    return;
  }
  if (esNodo(valor)) {
    const indice = contador.siguiente;
    contador.siguiente += 1;
    visitar(valor.id, indice);
    for (const [clave, val] of Object.entries(valor)) {
      if (clave === 'id' || clave === 'nodo') continue;
      recorrerEnOrdenEstructural(val, visitar, contador);
    }
  }
}

/**
 * @param {unknown} raizPrograma
 * @returns {Map<string, number>} `id` de nodo -> índice de recorrido estructural.
 */
function calcularIndicesPorId(raizPrograma) {
  /** @type {Map<string, number>} */
  const mapa = new Map();
  recorrerEnOrdenEstructural(raizPrograma, (id, indice) => mapa.set(id, indice), { siguiente: 0 });
  return mapa;
}

/**
 * @param {unknown} raizPrograma
 * @returns {Map<number, string>} índice de recorrido estructural -> `id` de nodo.
 */
function calcularIdsPorIndice(raizPrograma) {
  /** @type {Map<number, string>} */
  const mapa = new Map();
  recorrerEnOrdenEstructural(raizPrograma, (id, indice) => mapa.set(indice, id), { siguiente: 0 });
  return mapa;
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
function esErrorDeCuota(error) {
  if (typeof globalThis.DOMException !== 'undefined' && error instanceof globalThis.DOMException) {
    return (
      error.name === 'QuotaExceededError' ||
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      error.code === 22 ||
      error.code === 1014
    );
  }
  // Algunos entornos (p.ej. jsdom, motores antiguos) lanzan un Error simple
  // con un mensaje reconocible en lugar de un DOMException tipado.
  return (
    error instanceof Error && /quota/i.test(error.message)
  );
}

/**
 * Serializa y guarda el programa (IR lógica) y el estado visual (posiciones)
 * en `localStorage`, sustituyendo cualquier guardado previo.
 *
 * Degrada explícitamente en lugar de lanzar cuando el almacenamiento no
 * está disponible, está deshabilitado (modo privado), se excede la cuota,
 * o el diagrama supera `LIMITE_BYTES`: en todos esos casos el editor debe
 * poder seguir funcionando en memoria sin autoguardado.
 *
 * @param {unknown} programa Nodo `Programa` del modelo del editor (con `id`).
 * @param {EstadoVisual} estadoVisual Estado visual asociado (posiciones por id).
 * @param {string | null} [usuarioId] Id del usuario autenticado, para
 *   namespacear la clave y no compartir diagrama entre cuentas.
 * @param {string | null} [ambito] Slug del ejercicio al que pertenece este
 *   diagrama. Sin él, el borrador libre (ver `state/ambito-borrador.js`).
 * @returns {ResultadoGuardado}
 */
export function guardarProgramaLocal(programa, estadoVisual, usuarioId, ambito) {
  const storage = obtenerStorage();
  if (!storage) {
    return { ok: false, motivo: 'no_disponible' };
  }

  // `serializarPrograma` valida la entrada y lanza `TypeError` si `programa`
  // no es un nodo `Programa` válido: eso es un error de contrato del
  // llamador (no un problema de almacenamiento) y se deja propagar.
  const irJson = serializarPrograma(programa);

  // Las posiciones se guardan indexadas por índice de recorrido estructural,
  // no por `id` (ver nota de arquitectura al principio del archivo): el
  // `id` no sobrevive a un ciclo guardar → recargar → deserializar.
  const idAIndice = calcularIndicesPorId(programa);
  const posicionesPorId = estadoVisualAObjeto(estadoVisual);
  /** @type {Record<string, import('./editor-visual-state.js').PosicionVisual>} */
  const posicionesPorIndice = {};
  for (const [id, posicion] of Object.entries(posicionesPorId)) {
    const indice = idAIndice.get(id);
    // Una posición cuyo id ya no existe en el árbol actual (referencia
    // huérfana, p.ej. de un nodo borrado) se descarta silenciosamente: no
    // tiene ningún nodo al que reasociarse.
    if (indice === undefined) continue;
    posicionesPorIndice[String(indice)] = posicion;
  }

  const envoltorio = {
    version: VERSION_ESQUEMA,
    guardadoEn: new Date().toISOString(),
    programa: irJson,
    estadoVisual: posicionesPorIndice,
  };

  let serializado;
  try {
    serializado = JSON.stringify(envoltorio);
  } catch {
    return { ok: false, motivo: 'error_desconocido' };
  }

  const tamanoBytes = new globalThis.TextEncoder().encode(serializado).length;
  if (tamanoBytes > LIMITE_BYTES) {
    return { ok: false, motivo: 'excede_limite' };
  }

  try {
    storage.setItem(claveAlmacenamiento(usuarioId, ambito), serializado);
    return { ok: true };
  } catch (error) {
    if (esErrorDeCuota(error)) {
      return { ok: false, motivo: 'cuota_excedida' };
    }
    return { ok: false, motivo: 'error_desconocido' };
  }
}

/**
 * @param {unknown} valor
 * @returns {valor is { version: number, programa: Record<string, unknown>, estadoVisual: Record<string, unknown> }}
 */
function tieneFormaDeEnvoltorio(valor) {
  if (typeof valor !== 'object' || valor === null) return false;
  const objeto = /** @type {Record<string, unknown>} */ (valor);
  return (
    typeof objeto.version === 'number' &&
    typeof objeto.programa === 'object' &&
    objeto.programa !== null &&
    typeof objeto.estadoVisual === 'object' &&
    objeto.estadoVisual !== null
  );
}

/**
 * Lee y reconstruye el programa y el estado visual guardados en
 * `localStorage`, validando la forma de los datos.
 *
 * Si el almacenamiento no está disponible, no hay nada guardado, el JSON
 * está corrupto, la versión del esquema no coincide, o el contenido no
 * corresponde a un `Programa`/estado visual válido, se descarta el dato
 * (limpiando la clave para no dejar basura ni reintentar sobre el mismo
 * dato corrupto) y se devuelve `null` sin romper el editor.
 *
 * @param {string | null} [usuarioId] Id del usuario autenticado, para leer
 *   el diagrama namespaced de esa cuenta (ver `guardarProgramaLocal`).
 * @param {string | null} [ambito] Slug del ejercicio cuyo borrador se lee.
 *   Sin él, el borrador libre (ver `state/ambito-borrador.js`).
 * @returns {EstadoEditorGuardable | null}
 */
export function cargarProgramaLocal(usuarioId, ambito) {
  const storage = obtenerStorage();
  if (!storage) return null;

  const clave = claveAlmacenamiento(usuarioId, ambito);

  let bruto;
  try {
    bruto = storage.getItem(clave);
  } catch {
    return null;
  }
  if (bruto === null) return null;

  /** @type {unknown} */
  let envoltorio;
  try {
    envoltorio = JSON.parse(bruto);
  } catch {
    limpiarProgramaLocal(usuarioId, ambito);
    return null;
  }

  if (!tieneFormaDeEnvoltorio(envoltorio) || envoltorio.version !== VERSION_ESQUEMA) {
    limpiarProgramaLocal(usuarioId, ambito);
    return null;
  }

  try {
    const programa = deserializarPrograma(envoltorio.programa);

    // El envoltorio guarda las posiciones por índice de recorrido
    // estructural (ver nota de arquitectura al principio del archivo), no
    // por `id`: se traducen de vuelta a los ids NUEVOS que acaba de generar
    // `deserializarPrograma`, recorriendo el árbol reconstruido en el mismo
    // orden que se usó al guardar.
    const indiceAId = calcularIdsPorIndice(programa);
    const posicionesPorIndice = /** @type {Record<string, unknown>} */ (envoltorio.estadoVisual);
    /** @type {Record<string, import('./editor-visual-state.js').PosicionVisual>} */
    const posicionesPorId = {};
    for (const [indiceTexto, posicion] of Object.entries(posicionesPorIndice)) {
      const indice = Number(indiceTexto);
      const id = indiceAId.get(indice);
      // Índice fuera de rango (p.ej. de un diagrama guardado con más nodos
      // de los que tiene ahora el IR-JSON leído): se descarta esa posición
      // huérfana sin invalidar el resto del guardado.
      if (id === undefined) continue;
      posicionesPorId[id] = /** @type {import('./editor-visual-state.js').PosicionVisual} */ (posicion);
    }

    const estadoVisual = estadoVisualDesdeObjeto(posicionesPorId);
    return { programa, estadoVisual };
  } catch {
    // IR-JSON o estado visual con forma inesperada: datos corruptos o de
    // una versión incompatible que igual pasó la comprobación de `version`
    // por error de escritura previo. Se descarta sin romper el editor.
    limpiarProgramaLocal(usuarioId, ambito);
    return null;
  }
}

/**
 * Limpieza explícita del autoguardado local (criterio de aceptación:
 * "el store expone limpieza explícita"). Se usa tanto al descartar datos
 * inválidos como cuando el propio editor quiere olvidar el borrador local
 * (p.ej. tras guardar con éxito en la futura cuenta de usuario, TSK-0028).
 * No lanza si el almacenamiento no está disponible.
 * @param {string | null} [usuarioId] Id del usuario cuyo diagrama namespaced
 *   se limpia; sin él, limpia la clave base (compatibilidad/modo sin sesión).
 * @param {string | null} [ambito] Slug del ejercicio cuyo borrador se limpia.
 *   Sin él, el borrador libre (ver `state/ambito-borrador.js`).
 * @returns {void}
 */
export function limpiarProgramaLocal(usuarioId, ambito) {
  const storage = obtenerStorage();
  if (!storage) return;
  try {
    storage.removeItem(claveAlmacenamiento(usuarioId, ambito));
  } catch {
    // No hay nada razonable que hacer si ni siquiera `removeItem` funciona;
    // se ignora para no romper el flujo del editor.
  }
}

/**
 * Crea un programador de autoguardado con "debounce": agrupa cambios
 * rápidos y consecutivos del diagrama en un único guardado tras
 * `retrasoMs` de inactividad, en vez de guardar en cada pulsación/arrastre.
 *
 * Este módulo no decide CUÁNDO ha cambiado el diagrama (no toca el DOM ni
 * conoce el controller de la feature): el llamador invoca
 * `notificarCambio()` tras cada mutación del modelo/estado visual.
 *
 * @param {() => EstadoEditorGuardable} obtenerEstadoActual
 * @param {{ retrasoMs?: number, usuarioId?: string | null, ambito?: string | null }} [opciones]
 * @returns {{ notificarCambio: () => void, detener: () => void }}
 */
export function crearAutoguardadoLocal(obtenerEstadoActual, opciones = {}) {
  const retrasoMs = opciones.retrasoMs ?? 800;
  const usuarioId = opciones.usuarioId ?? null;
  const ambito = opciones.ambito ?? null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let temporizador;

  function notificarCambio() {
    if (temporizador !== undefined) {
      clearTimeout(temporizador);
    }
    temporizador = setTimeout(() => {
      temporizador = undefined;
      const { programa, estadoVisual } = obtenerEstadoActual();
      guardarProgramaLocal(programa, estadoVisual, usuarioId, ambito);
    }, retrasoMs);
  }

  function detener() {
    if (temporizador !== undefined) {
      clearTimeout(temporizador);
      temporizador = undefined;
    }
  }

  return { notificarCambio, detener };
}
